import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import { Design, RawMaterial, DesignFile, DesignVersion } from '@/types/design';

interface DesignState {
  designs: Design[];
  currentDesign: Design | null;
  versions: DesignVersion[];
  files: DesignFile[];
  isLoading: boolean;
  error: string | null;
  fetchDesigns: () => Promise<void>;
  fetchDesignDetails: (id: string) => Promise<void>;
  createDesign: (data: Partial<Design>) => Promise<Design>;
  updateDesign: (id: string, data: Partial<Design>) => Promise<void>;
  deleteDesign: (id: string) => Promise<void>;
  updateFile: (id: string, data: Partial<DesignFile>) => Promise<void>;
  saveRawMaterials: (fileId: string, materials: RawMaterial[]) => Promise<void>;
  getRawMaterials: (fileId: string) => Promise<RawMaterial[]>;
  uploadFile: (file: File, projectId: string, designId: string | null, description: string, taskId?: string) => Promise<void>;
}

export const useDesignStore = create<DesignState>((set, get) => ({
  designs: [],
  currentDesign: null,
  versions: [],
  files: [],
  isLoading: false,
  error: null,

  fetchDesigns: async () => {
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('designs')
        .select(`
          *,
          project:project_id (
            id,
            name
          ),
          creator:created_by (
            id,
            full_name
          ),
          department:department_id (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ designs: data || [] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch designs';
      set({ error: message });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchDesignDetails: async (id: string) => {
    try {
      set({ isLoading: true, error: null });
      
      // Fetch design details
      const { data: design, error: designError } = await supabase
        .from('designs')
        .select(`
          *,
          project:project_id (
            id,
            name
          ),
          creator:created_by (
            id,
            full_name
          ),
          department:department_id (
            id,
            name
          )
        `)
        .eq('id', id)
        .single();

      if (designError) throw designError;

      // Fetch versions
      const { data: versions, error: versionsError } = await supabase
        .from('design_versions')
        .select('*')
        .eq('design_id', id)
        .order('version', { ascending: false });

      if (versionsError) throw versionsError;

      // Fetch files
      const { data: files, error: filesError } = await supabase
        .from('design_files')
        .select('*')
        .eq('design_id', id)
        .order('created_at', { ascending: false });

      if (filesError) throw filesError;

      set({
        currentDesign: design,
        versions: versions || [],
        files: files || []
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch design details';
      set({ error: message });
    } finally {
      set({ isLoading: false });
    }
  },

  createDesign: async (data: Partial<Design>) => {
    try {
      set({ isLoading: true, error: null });

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Create design
      const { data: design, error } = await supabase
        .from('designs')
        .insert([{
          ...data,
          created_by: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      // Update local state
      set(state => ({
        designs: [design, ...state.designs]
      }));

      return design;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create design';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateDesign: async (id: string, data: Partial<Design>) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('designs')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        designs: state.designs.map(design =>
          design.id === id ? { ...design, ...data } : design
        ),
        currentDesign: state.currentDesign?.id === id
          ? { ...state.currentDesign, ...data }
          : state.currentDesign
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update design';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteDesign: async (id: string) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('designs')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        designs: state.designs.filter(design => design.id !== id),
        currentDesign: state.currentDesign?.id === id ? null : state.currentDesign
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete design';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateFile: async (id: string, data: Partial<DesignFile>) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('design_files')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        files: state.files.map(file =>
          file.id === id ? { ...file, ...data } : file
        )
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update file';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  saveRawMaterials: async (fileId: string, materials: RawMaterial[]) => {
    try {
      set({ isLoading: true, error: null });
      
      const { error } = await supabase.rpc('save_raw_materials', {
        p_file_id: fileId,
        p_materials: materials
      });

      if (error) throw error;

      // Update local state
      set(state => ({
        files: state.files.map(file =>
          file.id === fileId ? { ...file, raw_materials: materials } : file
        )
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save raw materials';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  getRawMaterials: async (fileId: string) => {
    try {
      set({ isLoading: true, error: null });
      
      const { data, error } = await supabase.rpc('get_raw_materials', {
        p_file_id: fileId
      });

      if (error) throw error;
      return data || [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get raw materials';
      set({ error: message });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  uploadFile: async (file: File, projectId: string, designId: string | null, description: string, taskId?: string) => {
    try {
      set({ isLoading: true, error: null });
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('User not authenticated');

      const filePath = `public/${projectId}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('design_files_storage') // Ensure this bucket exists and has correct policies
        .upload(filePath, file);

      if (uploadError) {
        console.error('Storage Upload Error:', uploadError);
        throw new Error(`Failed to upload file to storage: ${uploadError.message}`);
      }

      const { data: storageData } = supabase.storage
        .from('design_files_storage')
        .getPublicUrl(filePath);
        
      if (!storageData || !storageData.publicUrl) {
        throw new Error('Failed to get public URL for uploaded file.');
      }

      const newFileRecord = {
        design_id: designId, // Use designId if available
        project_id: projectId,
        task_id: taskId,
        file_name: file.name,
        file_path: filePath, // Store the path, not the full public URL, or adjust as needed
        public_url: storageData.publicUrl, // Store the public URL
        file_type: file.type,
        size: file.size,
        description: description,
        uploaded_by: user.id,
        status: 'draft', // Default status
      };

      const { data: dbFile, error: dbError } = await supabase
        .from('design_files')
        .insert(newFileRecord)
        .select('*, uploader_profile:uploaded_by(first_name, last_name, full_name)')
        .single();

      if (dbError) {
        console.error('DB Insert Error:', dbError);
        // Attempt to delete the orphaned file from storage if DB insert fails
        await supabase.storage.from('design_files_storage').remove([filePath]);
        throw new Error(`Failed to save file metadata to database: ${dbError.message}`);
      }

      if (dbFile) {
        // If the uploaded file is associated with the current design, update the store's files array
        if (designId && get().currentDesign?.id === designId) {
          set(state => ({
            files: [dbFile, ...state.files.filter(f => f.id !== dbFile.id)] // Prepend, ensure no duplicates if re-uploading logic changes
          }));
        } else if (!designId) {
          // If the file is project-specific but not tied to a specific design document yet,
          // you might have a separate list for project-level unassigned files or re-fetch project files.
          // For now, we are not adding it to the main 'files' list if no designId or if it doesn't match currentDesign.
          // This behavior might need refinement based on how you want to handle project-level files vs design-specific files.
        }
        // The main 'files' array in the store is the source of truth for FileList.
        // No need to separately update currentDesign.files as Design type doesn't have it.
      }

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload file';
      console.error('Upload File Overall Error:', err);
      set({ error: message });
      throw err; // Re-throw to be caught by the calling component's try/catch
    } finally {
      set({ isLoading: false });
    }
  }
}));

// Set up realtime subscription
supabase
  .channel('design_changes')
  .on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'designs' },
    () => {
      useDesignStore.getState().fetchDesigns();
    }
  )
  .subscribe();

export default useDesignStore;